'use client'

import { useState } from 'react'
import { noop } from 'lodash'

import { ErrorItem, ResponseError, ResponseCode } from '@marketplace-web/core-api/api-client-util'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  addAddress,
  AddressEntryType,
  AddressField,
  AddressFieldConfiguration,
  AddressFieldTranslationModel,
  AddressModel,
  addShippingAddress,
  addShippingOrderAddress,
  UserAddressResp,
} from '@marketplace-web/address/address-data'

import AddressForm from '../AddressForm'

type Props = {
  address: AddressModel | null
  transactionId?: number
  addressEntryType: AddressEntryType
  fieldConfiguration?: Array<AddressFieldConfiguration>
  translations?: AddressFieldTranslationModel
  shippingOrderId?: number | null
  onSubmit?: () => void
  onCancel?: () => void
}

const UserAddress = ({
  address,
  translations,
  addressEntryType,
  transactionId,
  fieldConfiguration = [
    { field: AddressField.Country },
    { field: AddressField.Name },
    { field: AddressField.Line1 },
    { field: AddressField.Line2 },
    { field: AddressField.PostalCode },
    { field: AddressField.City },
  ],
  shippingOrderId,
  onSubmit = noop,
  onCancel = noop,
}: Props) => {
  const [errors, setErrors] = useState<Array<ErrorItem>>([])
  const [submitState, setSubmitState] = useState<UiState>(UiState.Idle)

  const handleAddShippingOrderAddress = async (newAddress: AddressModel) => {
    const response = await addAddress({
      address: { ...newAddress, entryType: addressEntryType },
      skipShippingCallback: true,
    })

    if ('errors' in response) return response
    if (!shippingOrderId) return response

    const {
      user_address: { id: addressId },
    } = response

    const shippingOrderResponse = await addShippingOrderAddress({ addressId, shippingOrderId })

    if ('errors' in shippingOrderResponse) {
      return shippingOrderResponse
    }

    return response
  }

  async function handleSubmit(newAddress: AddressModel, isFormValid: boolean) {
    if (!isFormValid) return

    setErrors([])
    setSubmitState(UiState.Pending)

    const addressPayload = { ...newAddress, entryType: addressEntryType }
    let response: UserAddressResp | ResponseError<unknown>

    if (transactionId && !shippingOrderId) {
      response = await addShippingAddress({
        address: addressPayload,
        transactionId,
      })
    } else if (shippingOrderId) {
      response = await handleAddShippingOrderAddress(newAddress)
    } else {
      response = await addAddress({ address: addressPayload })
    }

    if ('errors' in response) {
      setSubmitState(UiState.Failure)

      const { message, code } = response

      let generalFieldErrors: Array<ErrorItem> = []

      if (message && code === ResponseCode.CountryChangeNotAllowed) {
        generalFieldErrors = [
          {
            field: AddressField.Country,
            value: response.message,
          },
        ]
      }

      setErrors([...response.errors, ...generalFieldErrors])

      return
    }

    onSubmit()
    setSubmitState(UiState.Success)
  }

  function renderLoader() {
    return <ContentLoader styling={ContentLoader.Styling.Wide} size={ContentLoader.Size.Large} />
  }

  const renderAddressForm = () => (
    <AddressForm
      defaultValues={address}
      errors={errors}
      fieldConfiguration={fieldConfiguration}
      translations={translations}
      loader={renderLoader()}
      isSubmitPending={submitState === UiState.Pending}
      onSubmit={handleSubmit}
      onCancel={onCancel}
    />
  )

  return renderAddressForm()
}

export default UserAddress
